import { useCalendar } from '@cocoonspace/shared/hooks/cocoon-api'
import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { useEffect, useState } from 'react'
import type { TimeSlot } from '../../templates/booking-stepper'
import { DraggableTimeslot } from './draggable-timeslot'
import { DraggableTimeslotContainer } from './draggable-timeslot-container'
import {
	TIME_SLOT_HEIGHT,
	type TimeslotBounds,
	calculateMinutes,
	calculateY,
	formatTime,
	parseTime,
	snapToGrid,
} from './helpers'
import { useCustomModifiers } from './use-custom-modifiers'

interface TimeSchedulerProps {
	spaceId: string
	eventId?: string
	value: TimeSlot
	isAvailable?: boolean
	notAvailableMsg?: string
	onTimeChange: (start: string, end: string) => void
}

const computeBoundsFromTimeSlot = (timeslot: TimeSlot) => {
	if (!timeslot.start || !timeslot.end) {
		return {
			startY: calculateY(parseTime('08:00')),
			height: TIME_SLOT_HEIGHT * 2,
		}
	}

	const startY = calculateY(parseTime(timeslot.start))
	const height = calculateY(parseTime(timeslot.end)) - startY

	return {
		startY,
		height,
	}
}

export const TimeScheduler = ({
	spaceId,
	eventId,
	value,
	isAvailable,
	onTimeChange,
}: TimeSchedulerProps) => {
	const [bounds, setBounds] = useState<TimeslotBounds>(() =>
		computeBoundsFromTimeSlot(value),
	)

	useEffect(() => {
		setBounds(computeBoundsFromTimeSlot(value))
	}, [value])

	const handleDragEnd = ({ active, delta }: DragEndEvent) => {
		setBounds((prev) => {
			let newStartY = prev.startY
			let newHeight = prev.height

			switch (active.id) {
				case 'draggable-timeslot':
					newStartY = prev.startY + delta.y
					break
				case 'draggable-timeslot-resize-top':
					newStartY = prev.startY + delta.y
					newHeight = prev.height - delta.y
					break
				case 'draggable-timeslot-resize-bottom':
					newHeight = prev.height + delta.y
					break
			}

			newStartY = snapToGrid(newStartY)
			newStartY = Math.max(0, newStartY)

			newHeight = snapToGrid(newHeight)
			newHeight = Math.max(TIME_SLOT_HEIGHT, newHeight)

			const newStartMinutes = calculateMinutes(newStartY)
			const newEndMinutes = calculateMinutes(newStartY + newHeight)
			const newStart = formatTime(newStartMinutes)
			const newEnd = formatTime(newEndMinutes)

			onTimeChange(newStart, newEnd)

			return { ...prev, startY: newStartY, height: newHeight }
		})

		setTimeout(() => setIsTransforming(false), 100)
	}

	const customModifiers = useCustomModifiers(bounds)

	const handleTimeChange = (newStart: string, newEnd: string) => {
		const newStartY = calculateY(parseTime(newStart))
		const newHeight = calculateY(parseTime(newEnd)) - newStartY

		setBounds({
			startY: newStartY,
			height: newHeight,
		})

		onTimeChange(newStart, newEnd)
	}

	const [isTransforming, setIsTransforming] = useState(false)

	const calendarQuery = useCalendar(spaceId, {
		ignore: eventId,
		day: value.day!,
	})

	return (
		<DndContext
			autoScroll={false}
			onDragStart={() => setIsTransforming(true)}
			onDragEnd={handleDragEnd}
			modifiers={[restrictToVerticalAxis, customModifiers]}
		>
			{calendarQuery.isSuccess && (
				<DraggableTimeslotContainer
					value={value}
					calendar={calendarQuery.data}
					bounds={bounds}
					onTimeChange={handleTimeChange}
				>
					<DraggableTimeslot
						isTransforming={isTransforming}
						value={value}
						isAvailable={isAvailable}
						bounds={bounds}
					/>
				</DraggableTimeslotContainer>
			)}
		</DndContext>
	)
}
